import angular from 'angular';

import r2a from '@web/angularIntegration/r2a';
import ChooseBreakTypeModalAngular from '@web/timeClock/ChooseBreakTypeModal/ChooseBreakTypeModalAngular';
import BackToSoonModalAngular from '@web/timeClock/BackTooSoonModal/BackTooSoonModalAngular';
import ClockOutModalAngular from '@web/timeClock/ClockOutModal/ClockOutModalAngular';
import ConfirmationStateModalAngular from '@web/timeClock/ConfirmationStateModal/ConfirmationStateModalAngular';
import { NotificationModalAngular } from '@web/dashboard/NotificationModal/NotificationModalAngular';
import ClockInNotAvailableModal from '@web/timeClock/ClockInNotAvailableModal/ClockInNotAvailableModal';

import config from '../config';
import { accessLevels } from '../constants';

import DashboardCtrl from './DashboardCtrl';
import home from './home.html';
import indexUrl from './index.html';
import introductionUrl from './introduction.html';
import buttonsUrl from './buttons.html';
import UsersModel from './models/Users';
import GroupsModel from './models/Groups';
import PersonasModel from './models/Personas';
import AnnouncementsModel from './models/Announcements';
import NotificationsModel from './models/Notifications';
import TimezonesModel from './models/Timezones';
import WagesModel from './models/Wages';
import PersonaCollectionModel from './models/PersonaCollection';
import NotificationCollectionModel from './models/NotificationCollection';
import NotificationModalImport from './services/NotificationModal';
import GroupsService from './services/GroupsService';
import nextShiftsComponent from './components/nextShiftsComponent';
import usersGroupComponent from './components/usersGroupComponent';
import editGroupHeaderComponent from './components/editGroupHeaderComponent';
import editGroupFooterButtonsComponent from './components/editGroupFooterButtonsComponent';
import groupSectionHeaderComponent from './components/groupSectionHeaderComponent';
import groupEmployeeTabComponent from './components/groupEmployeeTabComponent';
import dateOfBirthComponent from './components/dateOfBirthComponent';
import upcomingShiftsToClockInComponent from './components/upcomingShiftsToClockInComponent';
import multiselectOptions from './directives/multiselectOptions';
import timeclockNoteComponent from './components/timeclockNoteComponent';

const { ADMIN_SITE_URL, AUTH_COOKIE_NAME } = config;
const dashboardModule = angular.module('sling.dashboard', []);
export default dashboardModule.name;

dashboardModule.config(($stateProvider) => {
  $stateProvider.state('dashboard', {
    abstract: true,
    templateUrl: indexUrl,
    controller($state) {
      'ngInject';

      const hiddenAdminMenuStates = [
        'dashboard.manage.pages.create',
        'dashboard.manage.pages.edit',
      ];

      this.hideAdminMenu = function hideAdminMenu() {
        return hiddenAdminMenuStates.includes($state.current.name);
      };
    },
    controllerAs: 'ctrl',
    resolve: {
      personaCollection(PersonaCollection) {
        return PersonaCollection.loadData();
      },
    },
    onEnter(RateService) {
      RateService.triggerRateModal();
    },
    data: {
      title: 'Dashboard',
      access: accessLevels.user,
    },
  });

  $stateProvider.state('dashboard.home', {
    url: '/',
    templateUrl: home,
    controller: DashboardCtrl,
    controllerAs: 'ctrl',
    resolve: {
      displayMode() {
        return 'dashboard';
      },
    },
    onEnter(Authentication, AnnouncementService, $uibModal) {
      // async onEnter would pause the state transition
      (async () => {
        await AnnouncementService.show();
        const { adminOnboardingCompleted, dashboardHelpShown, onboardingComplete } =
          Authentication.user.settings;
        if (!dashboardHelpShown && (onboardingComplete || adminOnboardingCompleted)) {
          try {
            await $uibModal.open({
              windowClass: 'sling-tour-modal sling-animate',
              templateUrl: introductionUrl,
            }).result;
          } catch {
            Authentication.user.settings.dashboardHelpShown = true;
            Authentication.syncSettings();
          }
        }
      })();
    },
  });

  $stateProvider.state('dashboard.home.alias', {
    url: 'dashboard',
    onEnter($state) {
      // introducing alias so we can open app on dashboard with deep link
      $state.go('dashboard.home', {}, { location: 'replace' });
    },
  });

  $stateProvider.state('dashboard.history', {
    url: '/history',
    templateUrl: home,
    controller: DashboardCtrl,
    controllerAs: 'ctrl',
    resolve: {
      displayMode() {
        return 'history';
      },
    },
  });

  $stateProvider.state('dashboard.admin', {
    url: '/admin',
    onEnter($cookies) {
      'ngInject';

      const form = angular.element(`
        <form method="POST" action="${ADMIN_SITE_URL}">
          <input type="hidden" name="Authorization" value="${$cookies.get(AUTH_COOKIE_NAME)}" />
        </form>
      `)[0];

      document.body.appendChild(form);
      form.submit();
    },
  });

  if (__STAGING__) {
    $stateProvider.state('buttons', {
      url: '/buttons',
      templateUrl: buttonsUrl,
      controllerAs: 'ctrl',
      controller($scope, $timeout, $q) {
        this.testProgressButton = function testProgressButton(btn, time = 2000) {
          $scope[btn] = true;

          $timeout(() => {
            $scope[btn] = false;
          }, time);
        };

        this.testErrorProgressButton = function testErrorProgressButton() {
          return $q((resolve, reject) => {
            $timeout(() => {
              reject();
            }, 2000);
          });
        };
      },
      resolve: {
        personaCollection(Groups, Users, PersonaCollection) {
          return PersonaCollection.loadData();
        },
      },
      data: {
        title: 'Buttons',
        access: accessLevels.user,
      },
    });
  }
});

dashboardModule.factory('Users', UsersModel);
dashboardModule.factory('Groups', GroupsModel);
dashboardModule.factory('Personas', PersonasModel);
dashboardModule.factory('Announcements', AnnouncementsModel);
dashboardModule.factory('Notifications', NotificationsModel);
dashboardModule.factory('Timezones', TimezonesModel);
dashboardModule.factory('Wages', WagesModel);

dashboardModule.service('PersonaCollection', PersonaCollectionModel);
dashboardModule.service('NotificationCollection', NotificationCollectionModel);
dashboardModule.service('NotificationModal', NotificationModalImport);
dashboardModule.service('GroupsService', GroupsService);

dashboardModule.component('nextShifts', nextShiftsComponent);
dashboardModule.component('usersGroup', usersGroupComponent);
dashboardModule.component('editGroupHeader', editGroupHeaderComponent);
dashboardModule.component('editGroupFooterButtons', editGroupFooterButtonsComponent);
dashboardModule.component('groupSectionHeader', groupSectionHeaderComponent);
dashboardModule.component('groupEmployeeTab', groupEmployeeTabComponent);
dashboardModule.component('dateOfBirth', dateOfBirthComponent);
dashboardModule.component('upcomingShiftsToClockIn', upcomingShiftsToClockInComponent);
dashboardModule.component('timeclockNote', timeclockNoteComponent);
dashboardModule.component('rClockOutModal', r2a(ClockOutModalAngular));
dashboardModule.component('rConfirmationStateModal', r2a(ConfirmationStateModalAngular));
dashboardModule.component('rChooseBreakTypeModal', r2a(ChooseBreakTypeModalAngular));
dashboardModule.component('rBackTooSoonModal', r2a(BackToSoonModalAngular));
dashboardModule.component(
  'rNotificationModal',
  r2a(
    NotificationModalAngular,
    ['isOpen', '&onClose', 'notification'],
    ['Authentication', 'PersonaCollection']
  )
);
dashboardModule.component(
  'rClockInNotAvailableModal',
  r2a(ClockInNotAvailableModal, ['&onClose', 'upcomingShiftStartDate', 'isVisible', 'usTimeFormat'])
);

dashboardModule.directive('multiselectOptions', multiselectOptions);
